/**
 * Componente para gestionar el método de pago en un formulario de pedido.
 * @module CustomPayment
 */
import React from "react";
import { Grid, Box } from "@mui/material";
import CustomTextField from "../../CustomTextField";
import CustomAutocomplete from "../../CustomAutocomplete";
import { useOrder } from "../../../Context/Ordenes_v2/Order.context";
import { useQuote } from "../../../Context/Quote/Quote.context";
import { origenMarketingOptions } from "../../../Utils/origenMarketingOptions";

/**
 * Componente funcional para gestionar el método de pago en un formulario de pedido.
 * @returns {JSX.Element} Elemento JSX que representa el componente CustomPayment.
 */
const CustomPayment = ({ isQuote, branch }) => {
  const contextOrder = useOrder();
  const contextQuote = useQuote();
  // Validar si existen métodos de pago en branch
  const availablePaymentMethods = metodos.filter((method) =>
    branch?.payments?.some((paymentId) => paymentId === method.label),
  );
  // Obtiene el estado del formulario y la función para cambiarlo desde el contexto de pedidos
  const { form, handleChangeForm, handleCustomForm } = isQuote
    ? contextQuote
    : contextOrder;

  /**
   * Actividad asociada a la orden actual.
   * Solo se define si no es una cotización.
   * @type {number[] | undefined}
   */
  const activity = !isQuote ? contextOrder.activity : undefined;
  /**
   * Método de pago seleccionado desde el formulario.
   * Busca por coincidencia de label.
   * @type {{ label: string, [key: string]: any } | undefined}
   */
  const selectedPaymentFromForm = metodos.find(
    (method) => method.label === form?.payment,
  );
  /**
   * Lista final de métodos de pago disponibles.
   * - Si hay uno seleccionado en el formulario:
   *   - Se agrega a la lista si no existe previamente.
   * - Si no hay selección, se mantienen los disponibles.
   * @type {Array<{ label: string, [key: string]: any }>}
   */
  const finalPaymentMethods = selectedPaymentFromForm
    ? [
        ...availablePaymentMethods,
        ...(availablePaymentMethods.some(
          (m) => m.label === selectedPaymentFromForm.label,
        )
          ? []
          : [selectedPaymentFromForm]),
      ]
    : availablePaymentMethods;

  /**
   * Maneja la actualización del método de pago desde la UI.
   * @param {{ label?: string } | null | undefined} e - Evento o elemento seleccionado.
   * @returns {void}
   */
  const updatePayment = (e) => {
    handleCustomForm("payment", e?.label ?? "");
    if (!isQuote) {
      activity.push(3);
    }
  };
  /**
   * Actualiza el campo "originMarketing" en el formulario llamando a la función `handleCustomForm`.
   * Si el evento tiene una propiedad `label`, la utiliza como valor; de lo contrario, usa una cadena vacía.
   * @param {object} e - El evento o el objeto que contiene los datos del origen de marketing.
   * @param {string} [e.label] - La etiqueta asociada al objeto (opcional).
   */
  const updateOrigin = (e) => {
    handleCustomForm("origenMarketing", e?.label ?? "");
  };

  return (
    <div id="SECTION-3">
      <p style={design.title}>
        {isQuote ? "4.Observaciones" : "4. Método de pago"}
      </p>
      {/* Contenedor del formulario de método de pago */}
      <Grid container style={design.Card}>
        {isQuote ? null : (
          <>
            <Grid item xs={12} md={6} xl={6}>
              <CustomAutocomplete
                onChange={updatePayment}
                name="payment"
                value={form?.payment}
                label="Tipo de pago"
                options={finalPaymentMethods}
                grid={12}
                getOptionLabel={(option) => option.label}
                renderOption={(props, option) => (
                  <p {...props}>
                    {option.icon} &nbsp;&nbsp;
                    {option.label}
                  </p>
                )}
              />
              <CustomAutocomplete
                onChange={updateOrigin}
                name="Origen marketing"
                value={form?.origenMarketing}
                defaultValue={form?.origenMarketing}
                label={form?.origenMarketing || "Origen marketing"}
                options={origenMarketingOptions}
                grid={12}
                required={true}
                getOptionLabel={(option) => option.label || option} // Handle option labels
                renderOption={(props, option) => (
                  <p {...props}>
                    {option.icon} &nbsp;&nbsp;
                    {option.label}
                  </p>
                )}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={isQuote ? 12 : 6} xl={isQuote ? 12 : 6}>
          {/* Campo de texto para agregar observaciones */}
          <CustomTextField
            label="Observaciones"
            type="text"
            name="observaciones"
            value={form?.observaciones}
            onChange={handleChangeForm}
            grid={12}
            required={false}
            multiline={5}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default CustomPayment;

/**
 * design es un objeto que contiene estilos personalizados.
 * @type {Object}
 */
const design = {
  title: {
    textAlign: "left",
    fontWeight: "600",
    color: "#4496D2",
    fontSize: 23,
    lineHeight: "20px",
  },
  Card: {
    border: "2px solid #C6C6C6",
    borderRadius: 20,
    width: "95%",
    padding: 15,
  },
  From: {
    fontWeight: "600",
    color: "#4496D2",
    fontSize: 18,
  },
};

// Opciones para el tipo de pago
const metodos = [
  {
    icon: (
      <Box
        component="img"
        src="https://movicaremx.com/IMG/app_mobile/FormatPNG/Payments/Efectivo.png"
        alt="Efectivo"
        width={30}
        height={30}
      />
    ),
    label: "Efectivo",
    id: 1,
  },
  {
    icon: (
      <Box
        component="img"
        src="https://movicaremx.com/IMG/app_mobile/FormatPNG/Payments/Transferencia.png"
        alt="Transferencia"
        width={30}
        height={30}
      />
    ),
    label: "Transferencia",
    id: 2,
  },
  {
    icon: (
      <Box
        component="img"
        src="https://movicaremx.com/IMG/app_mobile/FormatPNG/Payments/icono_tarjeta.png"
        alt="Tarjeta de débito"
        width={30}
        height={30}
      />
    ),
    label: "Tarjeta de débito",
    id: 3,
  },
  {
    icon: (
      <Box
        component="img"
        src="https://movicaremx.com/IMG/app_mobile/FormatPNG/Payments/Credito.png"
        alt="Tarjeta de crédito"
        width={30}
        height={30}
      />
    ),
    label: "Tarjeta de crédito",
    id: 4,
  },
  {
    icon: (
      <Box
        component="img"
        src="https://movicaremx.com/IMG/app_mobile/FormatPNG/Services/A_Credito.png"
        alt="Crédito"
        width={30}
        height={30}
      />
    ),
    label: "Crédito",
    id: 5,
  },
];
